/** React Query hooks for workspace data + a boot effect.
 *
 * Patch 24 · workspace hydration hardening.
 * ------------------------------------------
 * Before this patch the boot flow silently swallowed authentication
 * failures — a stale JWT, a revoked session, or a wiped DB row would
 * leave the topbar stuck on "Select a workspace" with hydrated=false
 * indefinitely. This hook now:
 *
 *   1. Detects 401/404 on ``/me`` (session invalid) and clears the
 *      auth store so AuthGuard routes the visitor to /welcome.
 *   2. Marks ``hydrated = true`` as soon as the workspaces fetch
 *      resolves — even when the response is an empty array — so
 *      downstream hooks (e.g. useAutoCreateWorkspaceFromOnboarding)
 *      can proceed.
 *   3. Preserves the "initial null → authenticated user hydration
 *      must not erase freshly loaded workspace queries" invariant by
 *      only clearing state on explicit errors.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

import {
  createWorkspace,
  fetchCapabilityMatrix,
  fetchMe,
  fetchMembers,
  fetchWorkspace,
  listWorkspaces,
  setActiveCampaignCap,
  setOperatingMode,
  updateWorkspaceProfile,
} from "@/entities/workspace/api";
import type { CreateWorkspaceInput, OperatingMode } from "@/entities/workspace/api";
import type { WorkspaceProfile } from "@/entities/workspace/model";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { useAuthStore } from "@/entities/auth/store";
import { setAuthToken } from "@/shared/api/http";

/** Boots the store: hydrate ``me`` + workspaces, pick current workspace. */
export function useBootstrapWorkspace(): { ready: boolean; error: unknown } {
  const setMe = useWorkspaceStore((s) => s.setMe);
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace);
  const currentId = useWorkspaceStore((s) => s.currentWorkspaceId);
  const markHydrated = useWorkspaceStore((s) => s.markHydrated);
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const isGuest = useAuthStore((s) => s.guest.active && !s.accessToken);

  useEffect(() => {
    if (isGuest && currentId) selectWorkspace(null);
  }, [isGuest, currentId, selectWorkspace]);

  const meQuery = useQuery({ queryKey: ["me"], queryFn: fetchMe, enabled: !isGuest });
  const wsQuery = useQuery({
    queryKey: ["workspaces"],
    queryFn: listWorkspaces,
    enabled: !isGuest && !!meQuery.data,
  });

  useEffect(() => {
    if (meQuery.data) setMe(meQuery.data);
  }, [meQuery.data, setMe]);

  // Session-invalid detection: /me returned 401 or 404 → session is
  // dead. Clear the auth + workspace state so AuthGuard routes back
  // to /welcome and the topbar stops showing a broken shell.
  useEffect(() => {
    if (!meQuery.error) return;
    const status = (meQuery.error as { status?: number })?.status ?? 0;
    if (status === 401 || status === 404) {
      useAuthStore.getState().clear();
      setAuthToken(null);
      setWorkspaces([]);
      selectWorkspace(null);
      setMe(null);
      // Patch 24 Part IV — surface a single, dismissible notice so the
      // visitor understands why they were bounced to /welcome.
      toast.error("Your session expired. Please sign in again.", {
        id: "session-expired",
        duration: 5000,
      });
    }
  }, [meQuery.error, setWorkspaces, selectWorkspace, setMe]);

  useEffect(() => {
    if (!wsQuery.data) return;
    setWorkspaces(wsQuery.data);
    // Patch 34.1.2 · Part IV — Workspace context repair.
    // Order of preference:
    //   1. The workspace persisted in localStorage IF it still exists in
    //      the current workspaces list. Never overwrite a valid choice.
    //   2. Otherwise the MOST RECENTLY CREATED workspace — a founder
    //      who just onboarded a new business should land inside it, not
    //      inside a stale seed workspace.
    const persisted = currentId && wsQuery.data.find((w) => w.id === currentId);
    let preferred: string | null;
    if (persisted) {
      preferred = currentId;
    } else if (wsQuery.data.length > 0) {
      const sorted = [...wsQuery.data].sort((a, b) => {
        const ta = new Date(a.created_at ?? 0).getTime();
        const tb = new Date(b.created_at ?? 0).getTime();
        return tb - ta;
      });
      preferred = sorted[0]?.id ?? null;
    } else {
      preferred = null;
    }
    selectWorkspace(preferred);
    markHydrated();
  }, [wsQuery.data, currentId, selectWorkspace, setWorkspaces, markHydrated]);

  return {
    ready: hydrated,
    error: meQuery.error ?? wsQuery.error ?? null,
  };
}

export const useCurrentWorkspace = () =>
  useWorkspaceStore((s) =>
    s.currentWorkspaceId
      ? s.workspaces.find((w) => w.id === s.currentWorkspaceId) ?? null
      : null,
  );

/** Patch 34.1.12 — stable workspace state contract.
 *
 *  The four-phase discriminator every workspace-aware page should
 *  gate on. Prevents the "flash between loading, no workspace, and
 *  empty state" symptom by never surfacing a null workspace while
 *  the initial hydration is still in-flight.
 *
 *  Contract:
 *    - ``loading``  hydration hasn't completed yet. Show a skeleton.
 *    - ``error``    ``fetchMe`` or ``listWorkspaces`` failed with a
 *                   non-auth error. Show a recovery card with Retry.
 *    - ``missing``  hydration completed and the user genuinely has
 *                   no workspaces. Show the "Create your first
 *                   workspace" state.
 *    - ``ready``    workspace resolved; render the page.
 *
 *  Pages must NEVER redirect to ``/welcome`` from the ``loading`` or
 *  transient ``missing`` branches — that's what caused the observed
 *  "Orkexra → Select a workspace → Orkexra" oscillation.
 */
export type WorkspacePhase = "loading" | "error" | "missing" | "ready";

export interface WorkspaceState {
  phase: WorkspacePhase;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  error: unknown | null;
}

export const useWorkspaceState = (): WorkspaceState => {
  const workspace = useCurrentWorkspace();
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  return {
    phase: !hydrated
      ? "loading"
      : workspace
      ? "ready"
      : workspaces.length === 0
      ? "missing"
      : "loading",
    workspace,
    error: null,
  };
};

export const useCapabilityMatrix = (workspaceId: string | null) =>
  useQuery({
    queryKey: ["capabilities", workspaceId],
    queryFn: () => fetchCapabilityMatrix(workspaceId!),
    enabled: !!workspaceId,
  });

export const useWorkspaceDetail = (workspaceId: string | null) =>
  useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetchWorkspace(workspaceId!),
    enabled: !!workspaceId,
  });

export const useMembers = (workspaceId: string | null) =>
  useQuery({
    queryKey: ["members", workspaceId],
    queryFn: () => fetchMembers(workspaceId!),
    enabled: !!workspaceId,
  });

export const useCreateWorkspace = () => {
  const qc = useQueryClient();
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace);
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) => createWorkspace(input),
    onSuccess: (ws) => {
      const list = useWorkspaceStore.getState().workspaces;
      setWorkspaces([...list, ws]);
      selectWorkspace(ws.id);
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

// ── Sprint 1 — Project Symphony ────────────────────────────────────
/** Flip a workspace's operating mode between Manual and Autopilot.
 *  Optimistically updates the local store so the toggle feels instant. */
export const useSetOperatingMode = (workspaceId: string | null) => {
  const qc = useQueryClient();
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  return useMutation({
    mutationFn: (mode: OperatingMode) => setOperatingMode(workspaceId!, mode),
    onSuccess: (updated) => {
      const list = useWorkspaceStore.getState().workspaces;
      setWorkspaces(list.map((w) => (w.id === updated.id ? updated : w)));
      void qc.invalidateQueries({ queryKey: ["workspace", updated.id] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

// ── Patch 37.4.9 — Founder-editable workspace name + campaign cap ──
function _syncStore(updated: { id: string }): void {
  const list = useWorkspaceStore.getState().workspaces;
  useWorkspaceStore.getState().setWorkspaces(
    list.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)),
  );
}

export const useUpdateWorkspaceProfile = (workspaceId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profile: WorkspaceProfile) => updateWorkspaceProfile(workspaceId!, profile),
    onSuccess: (updated) => {
      _syncStore(updated);
      void qc.invalidateQueries({ queryKey: ["workspace", updated.id] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

export const useSetActiveCampaignCap = (workspaceId: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cap: number | null) => setActiveCampaignCap(workspaceId!, cap),
    onSuccess: (updated) => {
      _syncStore(updated);
      void qc.invalidateQueries({ queryKey: ["workspace", updated.id] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};
